import type { JSX } from "react";
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router-dom";
import { Download } from "lucide-react";
import { PORTFOLIO_PERIODS } from "@finance-os/shared-types/constants";
import { api } from "../api.js";
import { PageHeader } from "../components/PageHeader.js";
import { PageActions } from "../components/PageActions.js";
import { StatCard } from "../components/StatCard.js";
import { Money } from "../components/Money.js";
import { Gauge } from "../components/Gauge.js";
import { Field, Select } from "../components/Form.js";
import { Segmented } from "../components/Segmented.js";
import { AllocationPanel } from "../components/portfolio/AllocationPanel.js";
import { IncomePanel } from "../components/portfolio/IncomePanel.js";
import { MoversPanel } from "../components/portfolio/MoversPanel.js";
import { PerformancePanel } from "../components/portfolio/PerformancePanel.js";
import { PortfolioHeadline } from "../components/portfolio/PortfolioHeadline.js";
import { csvMoney, downloadCsv } from "../lib/csv.js";
import { portfolioParams, readSelection, writeSelection } from "../lib/portfolio-query.js";
import { usePortfolio } from "../lib/use-portfolio.js";
import { Card } from "../components/ui/card.js";

const PERIOD_OPTIONS = PORTFOLIO_PERIODS.map((value) => ({ value, label: value }));

export function StocksDashboard(): JSX.Element {
  const [search, setSearch] = useSearchParams();
  const selection = readSelection(search);

  const holdings = useQuery({ queryKey: ["holdings"], queryFn: () => api.holdings() });
  const cash = useQuery({ queryKey: ["buying-power"], queryFn: () => api.buyingPower() });

  // One evaluation instant per selection: every panel is asked for the same
  // `as_of`, so a period or account change refetches and re-renders them together.
  const asOf = useMemo(() => new Date().toISOString(), [selection.period, selection.accountId]);
  const query = portfolioParams(selection, asOf);
  const summary = usePortfolio("summary", query);
  const allocation = usePortfolio("allocation", query);
  const performance = usePortfolio("performance", query);
  const movers = usePortfolio("movers", query);
  const income = usePortfolio("income", query);

  const accountOptions = useMemo(() => {
    const names = new Map<string, string>();
    for (const h of holdings.data?.items ?? []) names.set(h.account_id, h.account_name);
    for (const c of cash.data?.items ?? []) names.set(c.account_id, c.account_name);
    return [...names.entries()].sort(([, a], [, b]) => a.localeCompare(b));
  }, [holdings.data, cash.data]);

  // The cash cards and top holdings follow the account filter too.
  const inScope = (row: { account_id: string }): boolean =>
    selection.accountId === null || row.account_id === selection.accountId;
  const cashItems = (cash.data?.items ?? []).filter(inScope);
  const allHoldings = (holdings.data?.items ?? []).filter(inScope);
  const lastUpdated = allHoldings[0]?.observed_at ?? null;

  const exportCsv = (): void => {
    downloadCsv(
      "portfolio.csv",
      ["Symbol", "Name", "Account", "Broker", "Class", "Currency", "Quantity", "ValueEUR", "UnrealizedPL_EUR", "Weight%"],
      allHoldings.map((h) => [
        h.symbol, h.name ?? "", h.account_name, h.provider ?? "", h.asset_class, h.currency,
        h.quantity, csvMoney(h.value_eur_minor), csvMoney(h.unrealized_pl_eur_minor), h.weight_pct.toFixed(2),
      ]),
    );
  };

  const top = useMemo(() => {
    const total = allHoldings.reduce((s, h) => s + h.value_eur_minor, 0);
    return allHoldings
      .map((h) => ({ ...h, weight_pct: total > 0 ? (h.value_eur_minor / total) * 100 : 0 }))
      .sort((a, b) => b.weight_pct - a.weight_pct)
      .slice(0, 8);
  }, [allHoldings]);

  return (
    <>
      <PageHeader title="Stocks">
        <PageActions
          updatedAt={lastUpdated}
          actions={[{ label: "Export holdings CSV", icon: Download, onClick: exportCsv }]}
        />
      </PageHeader>

      {/* Period + account */}
      <Card className="mb-4 flex flex-wrap items-end gap-3 p-4 [&_label]:mb-0">
        <div className="flex flex-col gap-1.5">
          <span className="text-muted-foreground text-xs">Period</span>
          <Segmented
            label="Period"
            value={selection.period}
            options={PERIOD_OPTIONS}
            onChange={(period) => {
              setSearch(writeSelection(search, { period }));
            }}
          />
        </div>
        <div className="w-full sm:w-56">
          <Field label="Account">
            <Select
              value={selection.accountId ?? ""}
              onChange={(e) => {
                setSearch(writeSelection(search, { accountId: e.target.value === "" ? null : e.target.value }));
              }}
            >
              <option value="">All accounts</option>
              {accountOptions.map(([id, name]) => (
                <option key={id} value={id}>
                  {name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
      </Card>

      <PortfolioHeadline query={summary} period={selection.period} />

      <div className="mb-4">
        <PerformancePanel query={performance} />
      </div>

      <div className="mb-4 grid gap-4 lg:grid-cols-2">
        <AllocationPanel query={allocation} />
        <MoversPanel query={movers} />
      </div>

      <div className="mb-4">
        <IncomePanel query={income} />
      </div>

      {/* Cash per currency */}
      {cashItems.length > 0 && (
        <div className="mb-6 grid grid-cols-[repeat(auto-fit,minmax(200px,1fr))] gap-4">
          {cashItems.map((r) => (
            <StatCard
              key={`${r.account_id}-${r.currency}`}
              label={`${r.account_name} · ${r.currency}`}
              value={<Money amountMinor={r.amount_minor} native={r.currency} />}
              sub={r.provider ?? undefined}
            />
          ))}
        </div>
      )}

      {/* Top holdings */}
      <Card className="p-5">
        <div className="mb-3 text-sm font-medium">Top holdings</div>
        {top.length === 0 ? (
          <p className="text-muted-foreground text-sm">
            {holdings.isLoading ? "Loading…" : "No positions yet — they appear after the next broker sync."}
          </p>
        ) : (
          <div className="flex flex-col gap-3">
            {top.map((h) => (
              <div key={`${h.account_id}-${h.security_id ?? h.symbol}`} className="flex items-center gap-3">
                <div className="w-28 shrink-0">
                  <div className="font-medium">{h.symbol}</div>
                  {h.name && <div className="text-muted-foreground truncate text-xs">{h.name}</div>}
                </div>
                <div className="min-w-0 flex-1">
                  <Gauge value={h.weight_pct / 100} tone="brand" />
                </div>
                <div className="w-16 shrink-0 text-right text-sm tabular-nums">{h.weight_pct.toFixed(1)}%</div>
                <div className="w-28 shrink-0 text-right font-medium tabular-nums">
                  <Money amountMinor={h.value_eur_minor} native="EUR" />
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </>
  );
}
